import {sendAnalyticsEvent} from '#/analytics/analytics';
import {ContentTile} from '#/atoms/content-tile';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {DeleteConfirmButtonCompound} from '#/components/buttons/delete-confirm-button-compound';
import {IconButton} from '#/components/buttons/icon-button';
import {ThumbsUpDownButton} from '#/components/buttons/thumbs-up-down-button';
import {LinkOpenGraph} from '#/components/land-comment/link-open-graph';
import {LandCommentActions} from '#/components/land-comment/use-land-comment-actions';
import {DateText} from '#/components/transaction/profile-link-transaction-tile';
import {LinkToUserPortfolio, UserAvatarName} from '#/components/user/link-avatar-name';
import {useBlockingLoginAction} from '#/hooks/use-blocking-login-action';
import {useIsUserAccount} from '#/hooks/use-is-user-account';
import {ClassName} from '#/utils/utils';
import {FlagIcon} from '@heroicons/react/24/solid';
import {FunctionComponent, useContext, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Tooltip} from 'react-tooltip';
import {useClickAway} from 'react-use';
import {LandComment} from 'shareland-common';

type LandCommentTileProps = {
  comment: LandComment;
  landCommentActions: LandCommentActions;
  refetchComments: () => void;
} & ClassName;

export const LandCommentTile: FunctionComponent<LandCommentTileProps> = ({
  comment: propsComment,
  className = '',
  landCommentActions: {onLike, onDislike, onFlag, onDelete},
  refetchComments,
}) => {
  // Props
  const userProfile = propsComment.userProfile;

  const {t} = useTranslation();

  // Context
  const {land, landMarketInfo} = useContext(ActionMenuContext);

  // External hooks
  const isUserAccount = useIsUserAccount(userProfile.account);
  const [comment, setComment] = useState<LandComment>(propsComment);

  // Internal hooks
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const toggleIsDeleteConfirmationOpen = () => {
    setIsDeleteConfirmationOpen(!isDeleteConfirmationOpen);
  };

  // Vars
  const ref = useRef<HTMLDivElement>(null);
  const deleteConfirmationOpenClassName = isDeleteConfirmationOpen ? 'opacity-30 pointer-events-none' : '';

  // Functions
  const onLikeClick = useBlockingLoginAction(async () => {
    const commentResult = await onLike(comment);
    if (!commentResult) return;
    sendAnalyticsEvent.propertyCommentInteraction({
      land,
      landMarketInfo,
      linkShared: !!comment.openGraphLink,
      interactionType: 'like',
    });
    setComment(commentResult);
  });

  const onDislikeClick = useBlockingLoginAction(async () => {
    const commentResult = await onDislike(comment);
    if (!commentResult) return;
    sendAnalyticsEvent.propertyCommentInteraction({
      land,
      landMarketInfo,
      linkShared: !!comment.openGraphLink,
      interactionType: 'dislike',
    });
    setComment(commentResult);
  });

  const onFlagClick = useBlockingLoginAction(async () => {
    const commentResult = await onFlag(comment);
    if (!commentResult) return;
    sendAnalyticsEvent.propertyCommentInteraction({
      land,
      landMarketInfo,
      linkShared: !!comment.openGraphLink,
      interactionType: 'flag',
    });
    setComment(commentResult);
  });

  const onDeleteClick = async () => {
    await onDelete(comment);
    refetchComments();
  };

  const onUserClick = () => {
    sendAnalyticsEvent.userPortfolioConsultation({
      account: userProfile.account,
      hasProfilePicture: !!userProfile.avatarUrl,
      hasUserName: !!userProfile.displayName,
    });
  };

  // Effects
  useClickAway(ref, () => {
    setIsDeleteConfirmationOpen(false);
  });

  return (
    <ContentTile ref={ref} className={`flex flex-col gap-4 ${className}`}>
      <div className={`flex justify-between items-center ${deleteConfirmationOpenClassName}`}>
        <LinkToUserPortfolio
          onClick={onUserClick}
          account={userProfile.account}
          className='grow flex gap-2 justify-between items-center'
        >
          <UserAvatarName account={userProfile.account} userProfile={userProfile} />
          <DateText date={new Date(propsComment.date)} />
        </LinkToUserPortfolio>
      </div>

      <div className={`text-sm text-gray-400 break-words ${deleteConfirmationOpenClassName}`}>{comment.text}</div>

      {comment.openGraphLink && (
        <LinkOpenGraph openGraphLink={comment.openGraphLink} className={deleteConfirmationOpenClassName} />
      )}

      <div className='flex justify-between'>
        <div className={`space-x-4 ${deleteConfirmationOpenClassName}`}>
          <ThumbsUpDownButton
            direction='up'
            value={comment.likeCount}
            isActive={comment.likedByUser === 1}
            onClick={onLikeClick}
          />
          <ThumbsUpDownButton
            direction='down'
            value={comment.dislikeCount}
            isActive={comment.likedByUser === -1}
            onClick={onDislikeClick}
          />
        </div>
        {isUserAccount ? (
          <DeleteConfirmButtonCompound
            isOpen={isDeleteConfirmationOpen}
            toggleIsOpen={toggleIsDeleteConfirmationOpen}
            onDelete={onDeleteClick}
          />
        ) : (
          <>
            <IconButton
              IconComponent={FlagIcon}
              iconClassName={`w-4 h-4 group-hover:stroke-white group-hover:fill-white ${
                comment.flaggedByUser ? 'stroke-deepblue fill-deepblue' : 'stroke-gray-400 fill-transparent'
              }`}
              onClick={onFlagClick}
              data-tooltip-id='flag-comment'
            />
            <Tooltip
              id={'flag-comment'}
              style={{
                zIndex: 50,
                borderRadius: '6px',
                backgroundColor: '#3870FF',
              }}
              render={() => (
                <span className='text-sm font-semibold'>
                  {t('pages.actionMenu.information.sections.community.landCommentTile.flagTooltip')}
                </span>
              )}
            />
          </>
        )}
      </div>
    </ContentTile>
  );
};
